import { useCallback, useMemo, useState } from "react";
import { LIBRARY_SEARCH_LIMIT, searchLibrary, type LibrarySearchItem } from "../../../api/content/searchApi";
import type { ItemKind } from "../../../api/content/reviewDecoders";
import { useQueryAfterTypingPause } from "../../../shared/loading/afterTypingPause";
import { useLoad, type LoadState } from "../../../shared/loading/useLoad";

/** What narrows the found items beyond the query: one item kind, or one artifact content type. */
export type LibraryFilter =
  | { readonly by: "kind"; readonly kind: ItemKind }
  | { readonly by: "contentType"; readonly contentType: string };

export interface LibrarySearch {
  readonly query: string;
  readonly setQuery: (query: string) => void;
  /** The kinds searched, at least one; the server searches any kind named. */
  readonly kinds: readonly ItemKind[];
  /** `null` for everything `kinds` allows. */
  readonly filter: LibraryFilter | null;
  readonly setFilter: (filter: LibraryFilter | null) => void;
  /** The content types among the library's first `LIBRARY_SEARCH_LIMIT` artifacts, sorted; empty where artifacts aren't searched. */
  readonly contentTypes: readonly string[];
  /** Blank query: the items by name; else the items found. */
  readonly results: LoadState<readonly LibrarySearchItem[]>;
  readonly reload: () => void;
}

const NOTHING: Promise<null> = Promise.resolve(null);
const NO_ITEMS: readonly LibrarySearchItem[] = [];

/**
 * The library's one search (F38, P6): DMW's search syntax over names, descriptions and
 * content, for the kinds a place takes, narrowed by kind or by an artifact's content type.
 * It reads `main` only, as the server's library search does. Nothing loads until `enabled`.
 * `kinds` names at least one kind and keeps its identity between renders (a constant), as
 * it keys the request.
 */
export function useLibrarySearch(projectId: string, kinds: readonly ItemKind[], enabled: boolean): LibrarySearch {
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState<LibraryFilter | null>(null);
  const searched = useQueryAfterTypingPause(query);
  const searchesArtifacts = kinds.includes("artifact");
  const typeListing = useLoad(useCallback(
    (signal: AbortSignal) => enabled && searchesArtifacts
      ? searchLibrary(projectId, { query: "", kinds: ["artifact"], contentTypes: [], limit: LIBRARY_SEARCH_LIMIT }, signal)
      : NOTHING,
    [projectId, enabled, searchesArtifacts],
  ));
  const found = useLoad(useCallback(
    (signal: AbortSignal) => {
      if (!enabled) return NOTHING;
      const wantedKinds: readonly ItemKind[] = filter === null ? kinds : filter.by === "kind" ? [filter.kind] : ["artifact"];
      const contentTypes = filter?.by === "contentType" ? [filter.contentType] : [];
      return searchLibrary(projectId, { query: searched, kinds: wantedKinds, contentTypes, limit: LIBRARY_SEARCH_LIMIT }, signal);
    },
    [projectId, enabled, searched, filter, kinds],
  ));
  const listed = typeListing.state.status === "loaded" ? typeListing.state.value : null;
  const contentTypes = useMemo(
    () => [...new Set((listed ?? []).flatMap((item) => item.contentType === null ? [] : [item.contentType]))].sort(),
    [listed],
  );
  const results: LoadState<readonly LibrarySearchItem[]> = found.state.status !== "loaded" ? found.state : { status: "loaded", value: found.state.value ?? NO_ITEMS };
  return { query, setQuery, kinds, filter, setFilter, contentTypes, results, reload: found.reload };
}
